import React from 'react';
import { Lock, Clock, AlertTriangle, FileText } from 'lucide-react';
import {
    PRICE_LABEL,
    PRIMARY_UPGRADE_CTA,
    POST_CTA_SUPPORTING_LINE,
    ONE_TIME_PAYMENT_LABEL,
    PROVISIONAL_REPORT_CTA,
} from '../lib/commercialCopy';

interface Props {
    onUpgrade: () => void;
    /** Server-provided contextual upgrade message (auditResponse.upgrade.message) */
    upgradeMessage?: string;
    /** 'likely_taxable' | 'unlikely_taxable' | 'unknown' — drives the bridge line */
    thresholdStatus?: string | null;
    /** Server-rounded headline IHT figure. When 0, the CTA shifts to a
     *  confirmation/validation framing instead of "See your exact tax". */
    estimatedIht?: number | null;
    /** WP1: trust property may be missing — the full report is provisional, so nothing here promises an exact or confirmed figure. */
    trustEstateInclusionUnresolved?: boolean;
}

/**
 * Shown to free users in place of the full Next Steps + gated sections.
 * Editorial paywall with serif heading and italic disclaimer.
 */
export const LockedNextStepsPreview: React.FC<Props> = ({ onUpgrade, upgradeMessage, thresholdStatus, estimatedIht, trustEstateInclusionUnresolved = false }) => {
    const unresolved = trustEstateInclusionUnresolved === true;
    const isZeroTax = estimatedIht === 0;
    const ctaLabel = unresolved
        ? PROVISIONAL_REPORT_CTA
        : isZeroTax
        ? 'Confirm your result and see the full breakdown'
        : PRIMARY_UPGRADE_CTA;
    const message =
        upgradeMessage ||
        'Right now, you have an estimate. The full report shows you exactly how to handle the estate step by step — including deadlines, documents, and risks.';

    const earlyValueSignal =
        unresolved
            ? 'The full report shows the provisional calculation, which trust information is missing, and why a specialist should review it.'
            : thresholdStatus === 'unlikely_taxable'
            ? 'This confirms how the inheritance tax position is calculated and why no tax may be due.'
            : 'This shows how much inheritance tax may actually be due and how it is calculated.';

    const bridgeLine =
        unresolved
            ? 'Trust property may be missing from this calculation, so the tax position cannot be confirmed until a specialist has reviewed the trust.'
            : thresholdStatus === 'likely_taxable'
            ? 'Your current estimate is incomplete — the exact tax position depends on allowances, gifts, and trust structures.'
            : thresholdStatus === 'unlikely_taxable'
                ? 'You can still review the full calculation and confirm how the result is determined.'
                : 'The current estimate does not account for all factors that may affect the final tax position.';

    return (
        <div className="space-y-2">
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] px-1 flex items-center gap-1.5">
                <Lock className="w-3 h-3" /> Next steps
            </p>
            <div className="bg-white border border-[#E8E4DB] overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-6 pb-4">
                    <h3 className="font-serif text-[22px] font-normal tracking-[-0.01em] text-[#1A1A1A] m-0">
                        {unresolved ? 'See the provisional calculation and what is missing' : 'See your exact inheritance tax position'}
                    </h3>
                    <p className="text-[13px] text-[#6B6762] mt-1 leading-relaxed">
                        {message}
                    </p>
                    <p className="text-[13px] text-[#3F3D39] leading-relaxed mt-3 mb-4">
                        {earlyValueSignal}
                    </p>
                    <p className="font-serif italic text-[13px] text-[#6B6762] leading-relaxed">
                        Unlock the full report to access these.
                    </p>
                </div>

                {/* Step previews — quietly tease real content */}
                <div className="px-6 pb-2 relative">
                    <div className="space-y-2 opacity-40 select-none pointer-events-none" aria-hidden>
                        {[
                            { icon: Clock, text: 'IHT payment deadline — check timeline' },
                            { icon: AlertTriangle, text: 'Executor risk review — liability checks' },
                            { icon: FileText, text: 'Required documents — evidence list' },
                        ].map(({ icon: Icon, text }, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-3 p-3 border border-[#E8E4DB] bg-white"
                                style={{ filter: 'blur(2px)' }}
                            >
                                <div className="w-7 h-7 bg-[#F3EFE6] flex items-center justify-center shrink-0">
                                    <Icon className="w-3.5 h-3.5 text-[#AFA99F]" />
                                </div>
                                <span className="text-[13px] text-[#3F3D39]">{text}</span>
                            </div>
                        ))}
                    </div>

                    {/* Overlay lock */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-2 flex items-center gap-2 border border-[#E8E4DB]">
                            <Lock className="w-4 h-4 text-[#AFA99F]" />
                            <span className="text-[13px] text-[#3F3D39]">Locked</span>
                        </div>
                    </div>
                </div>

                {/* Value proposition + CTA */}
                <div className="px-6 py-5 border-t border-[#E8E4DB] bg-[#FAFAF8]">
                    <p className="text-[13px] text-[#6B6762] leading-relaxed mb-4">
                        {bridgeLine}
                    </p>
                    <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-3">
                        What you'll see in the full report
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-5">
                        {[
                            ...(unresolved
                                ? ['The provisional inheritance tax calculation', 'Which trust information is missing']
                                : ['Your exact inheritance tax calculation', 'How much tax may actually be due']),
                            'How allowances (NRB, RNRB) are applied',
                            'Impact of trusts and lifetime gifts',
                            'Step-by-step breakdown of the calculation',
                            'Clear guidance on what to do next',
                            'A complete report you can download and keep',
                        ].map((feature, i) => (
                            <div key={i} className="flex items-baseline gap-2 text-[13px] text-[#3F3D39]">
                                <span className="font-serif text-[#3730A3] shrink-0">✓</span>
                                {feature}
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="flex flex-col gap-1">
                            <button
                                onClick={onUpgrade}
                                className="bg-[#3730A3] text-white hover:bg-[#2E2780] px-5 py-3 text-[13px] font-medium tracking-[0.02em] transition-colors"
                            >
                                {ctaLabel} — {PRICE_LABEL}
                            </button>
                            <span className="text-[11px] text-[#6B6762] italic">
                                {POST_CTA_SUPPORTING_LINE}.
                            </span>
                        </div>
                        <div className="text-[11px] text-[#AFA99F] italic leading-relaxed">
                            <span>{ONE_TIME_PAYMENT_LABEL}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
